import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { ACCESS_TOKEN_STORAGE_KEY } from '@/api';
import { useNavigate, Link } from 'react-router-dom';
import { getDashboard, normalizeDashboard } from '@/services/demaxtoreApi';
import { listPortalPurchaseOrders, normalizePortalPurchaseOrdersList } from '@/services/portalPurchaseOrders';
import { COMMODITYBID_EXTERNAL_URL } from '@/config/portalUrls';
import CommodityBidDashboardSection from '@/components/customer/CommodityBidDashboardSection';
import { normalizePlatformAccess } from '@/lib/utils';
import {
  Package,
  TrendingUp,
  Truck,
  ArrowRight,
  FileText,
  Plus,
  Upload,
  ClipboardCheck,
  BellRing,
  Navigation,
  History,
  Search,
  Anchor,
} from 'lucide-react';

function formatApiError(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object' && typeof d.message === 'string') return d.message;
  const m = e?.response?.data?.message;
  if (typeof m === 'string') return m;
  if (typeof e?.message === 'string') return e.message;
  return fallback;
}

function formatDateShort(value) {
  if (value == null || value === '') return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function orderRef(o) {
  return o?.reference ?? o?.order_reference ?? o?.po_reference ?? o?.id ?? o?._id ?? '—';
}

function orderRefShort(o) {
  const raw = orderRef(o);
  if (raw === '—' || raw == null) return '—';
  const s = String(raw);
  if (s.length <= 16) return s;
  return `${s.slice(0, 8)}…${s.slice(-4)}`;
}

function rfqQuoteStatus(r) {
  return r.quote_status ?? r.quoteStatus ?? '—';
}

function NavRow({ icon: Icon, label, onClick, href, external, testId, muted }) {
  const cls = `flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
    muted
      ? 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
      : 'font-medium text-slate-800 hover:bg-slate-50'
  }`;
  const body = (
    <>
      {Icon && <Icon className="h-4 w-4 shrink-0 text-slate-400" strokeWidth={1.75} />}
      <span>{label}</span>
    </>
  );
  if (href) {
    return (
      <a
        href={href}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        data-testid={testId}
        className={cls}
      >
        {body}
      </a>
    );
  }
  return (
    <button type="button" data-testid={testId} className={cls} onClick={onClick}>
      {body}
    </button>
  );
}

export default function CustomerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [pendingQuotes, setPendingQuotes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const platform = normalizePlatformAccess(user);

  useEffect(() => {
    if (!localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY)?.trim()) {
      navigate('/login', { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [dashResult, poResult] = await Promise.allSettled([getDashboard(), listPortalPurchaseOrders()]);
        if (cancelled) return;
        if (dashResult.status === 'fulfilled') {
          const n = normalizeDashboard(dashResult.value.data);
          setRfqs(n.rfqs);
          setPendingQuotes(n.pendingQuotes);
        }
        if (poResult.status === 'fulfilled') {
          setPurchaseOrders(normalizePortalPurchaseOrdersList(poResult.value.data));
        }
        if (dashResult.status === 'rejected' && poResult.status === 'rejected') {
          setError(formatApiError(dashResult.reason, 'Could not load dashboard.'));
        } else if (dashResult.status === 'rejected') {
          setError(formatApiError(dashResult.reason, 'Özet yüklenemedi; PO listesi yerel olabilir.'));
        }
      } catch (e) {
        if (!cancelled) {
          setError(formatApiError(e, 'Could not load dashboard.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const greeting =
    new Date().getHours() < 12
      ? 'Günaydın'
      : new Date().getHours() < 17
        ? 'İyi günler'
        : 'İyi akşamlar';
  const firstName = user?.name?.split(' ')[0] ?? '';
  const previewPo = purchaseOrders.slice(0, 5);
  const previewRfqs = rfqs.slice(0, 5);

  const hasPlatformStrip = platform.demaxtore || platform.commoditybid || platform.freightiq;

  return (
    <div
      data-testid="customer-dashboard"
      className="relative mx-auto max-w-4xl space-y-8 pb-2 pt-1"
    >
      {/* Ambient */}
      <div
        className="pointer-events-none absolute -top-24 left-1/2 h-64 w-[min(100%,42rem)] -translate-x-1/2 rounded-full bg-sky-100/40 blur-3xl"
        aria-hidden
      />

      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl border border-slate-200/80 bg-white px-6 py-8 shadow-[0_2px_24px_-8px_rgba(15,23,42,0.12)] sm:px-8 sm:py-9">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <h1 className="font-heading text-[1.65rem] font-semibold leading-tight tracking-tight text-slate-900 sm:text-3xl">
              {greeting}, {firstName}
            </h1>
            <p className="text-[15px] text-slate-500">
              {user?.company_name}
              <span className="mx-2 text-slate-300">·</span>
              <span className="text-slate-400">Özet ekranı</span>
            </p>
          </div>

          {platform.demaxtore && !loading && (
            <div className="grid grid-cols-3 gap-4 border-t border-slate-100 pt-6 sm:gap-6 sm:border-t-0 sm:pt-0 lg:border-l lg:border-slate-100 lg:pl-10">
              <div>
                <p className="text-2xl font-semibold tabular-nums text-slate-900 sm:text-3xl">{purchaseOrders.length}</p>
                <p className="mt-1 text-xs text-slate-500">PO</p>
              </div>
              <div>
                <p className="text-2xl font-semibold tabular-nums text-slate-900 sm:text-3xl">{rfqs.length}</p>
                <p className="mt-1 text-xs text-slate-500">RFQ</p>
              </div>
              <div>
                <p className="text-2xl font-semibold tabular-nums text-slate-900 sm:text-3xl">{pendingQuotes}</p>
                <p className="mt-1 text-xs text-slate-500">Bekleyen teklif</p>
              </div>
            </div>
          )}
        </div>

        {loading && (
          <div className="mt-8 flex gap-3" data-testid="dashboard-loading">
            <div className="h-12 flex-1 animate-pulse rounded-2xl bg-slate-100" />
            <div className="h-12 w-24 animate-pulse rounded-2xl bg-slate-100" />
          </div>
        )}
      </div>

      {error && (
        <div
          className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800"
          data-testid="dashboard-error"
        >
          {error}
        </div>
      )}

      {/* Platforms — tek panel, üç sütun */}
      {hasPlatformStrip && !loading && (
        <section className="relative overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_2px_20px_-6px_rgba(15,23,42,0.08)]">
          <div className="grid divide-y divide-slate-100 lg:grid-cols-3 lg:divide-x lg:divide-y-0">
            {platform.demaxtore && (
              <div className="p-6 sm:p-7">
                <div className="mb-5 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-sky-500 text-white shadow-sm shadow-sky-500/25">
                    <Package className="h-5 w-5" strokeWidth={1.75} />
                  </span>
                  <div>
                    <h2 className="font-heading text-base font-semibold text-slate-900">DeMaxtore</h2>
                    <p className="text-xs text-slate-500">Ticaret ve operasyon</p>
                  </div>
                </div>
                <nav className="flex flex-col gap-0.5">
                  <NavRow icon={FileText} label="RFQ’larım" onClick={() => navigate('/demaxtore/rfqs')} testId="dashboard-demaxtore-rfqs" />
                  <NavRow icon={Upload} label="Purchase order" onClick={() => navigate('/demaxtore/purchase-orders')} testId="dashboard-demaxtore-po" />
                  <NavRow icon={ClipboardCheck} label="Muayene raporu" onClick={() => navigate('/demaxtore/inspection-report')} testId="dashboard-demaxtore-inspection" muted />
                  <NavRow icon={BellRing} label="Bildirimler" onClick={() => navigate('/demaxtore/notifications')} testId="dashboard-demaxtore-notifications" muted />
                  <NavRow icon={Navigation} label="Canlı takip" onClick={() => navigate('/demaxtore/live-shipment-tracking')} testId="dashboard-demaxtore-live-tracking" muted />
                </nav>
              </div>
            )}

            {platform.commoditybid && (
              <div className="p-6 sm:p-7">
                <div className="mb-5 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-500 text-white shadow-sm shadow-orange-500/25">
                    <TrendingUp className="h-5 w-5" strokeWidth={1.75} />
                  </span>
                  <div>
                    <h2 className="font-heading text-base font-semibold text-slate-900">CommodityBid</h2>
                    <p className="text-xs text-slate-500">Teklif ve ihale</p>
                  </div>
                </div>
                <nav className="flex flex-col gap-0.5">
                  <NavRow icon={Plus} label="Teklif oluştur" href={COMMODITYBID_EXTERNAL_URL} external testId="dashboard-create-commodity-bid" />
                  <NavRow icon={ArrowRight} label="Bekleyen teklifler" onClick={() => navigate('/commoditybid/live-bids')} testId="dashboard-pending-bids" muted />
                  <NavRow icon={History} label="Tamamlanan teklifler" onClick={() => navigate('/commoditybid/history')} testId="dashboard-completed-bids" muted />
                </nav>
              </div>
            )}

            {platform.freightiq && (
              <div className="p-6 sm:p-7">
                <div className="mb-5 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-sm shadow-emerald-600/25">
                    <Truck className="h-5 w-5" strokeWidth={1.75} />
                  </span>
                  <div>
                    <h2 className="font-heading text-base font-semibold text-slate-900">FreightIQ</h2>
                    <p className="text-xs text-slate-500">Lojistik</p>
                  </div>
                </div>
                <nav className="flex flex-col gap-0.5">
                  <NavRow icon={Search} label="Freight arama" onClick={() => navigate('/freightiq/requests')} testId="dashboard-freight-search" />
                  <NavRow icon={Anchor} label="Freight teklifleri" onClick={() => navigate('/freightiq/offers')} testId="dashboard-freight-offers" muted />
                  <NavRow icon={History} label="Freight geçmişi" onClick={() => navigate('/freightiq/history')} testId="dashboard-freight-history" muted />
                </nav>
              </div>
            )}
          </div>
        </section>
      )}

      {platform.commoditybid && !loading && <CommodityBidDashboardSection />}

      {/* Son hareketler — liste, tablo yok */}
      {platform.demaxtore && !loading && (
        <section className="space-y-5">
          <h2 className="text-sm font-semibold text-slate-900">Son hareketler</h2>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-3xl border border-slate-200/80 bg-white p-5 shadow-sm sm:p-6">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm font-medium text-slate-800">Purchase order</span>
                <Link to="/demaxtore/purchase-orders" className="text-xs font-medium text-sky-600 hover:text-sky-700">
                  Tümü
                </Link>
              </div>
              {previewPo.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-500">Henüz kayıt yok</p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {previewPo.map((o, i) => {
                    const refFull = String(orderRef(o));
                    return (
                      <li
                        key={refFull + i}
                        className="flex items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-slate-50/50 px-4 py-3"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-mono text-xs font-medium text-slate-800" title={refFull}>
                            {orderRefShort(o)}
                          </p>
                          <p className="mt-0.5 truncate text-xs text-slate-500" title={o.file_name ?? o.filename ?? ''}>
                            {o.file_name ?? o.filename ?? '—'}
                          </p>
                        </div>
                        <span className="shrink-0 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] capitalize text-slate-600">
                          {o.status ?? '—'}
                        </span>
                        <span className="hidden shrink-0 text-xs text-slate-400 sm:inline">{formatDateShort(o.uploaded_at ?? o.created_at)}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="rounded-3xl border border-slate-200/80 bg-white p-5 shadow-sm sm:p-6">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm font-medium text-slate-800">RFQ</span>
                <Link to="/demaxtore/rfqs" className="text-xs font-medium text-sky-600 hover:text-sky-700">
                  Tümü
                </Link>
              </div>
              {previewRfqs.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-500">Kayıt yok</p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {previewRfqs.map((r, i) => (
                    <li
                      key={(r.id ?? r._id ?? i) + String(i)}
                      className="flex items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-slate-50/50 px-4 py-3"
                    >
                      <div className="min-w-0 flex-1">
                        <p
                          className="truncate text-xs font-semibold text-slate-900"
                          title={r.title != null && String(r.title).trim() ? String(r.title) : ''}
                        >
                          {r.title != null && String(r.title).trim() ? String(r.title) : '—'}
                        </p>
                        <p className="mt-0.5 text-xs font-medium capitalize text-slate-600">{r.status ?? '—'}</p>
                        <p className="mt-0.5 text-xs text-slate-500 capitalize">{String(rfqQuoteStatus(r))}</p>
                      </div>
                      <span className="shrink-0 text-xs text-slate-400">{formatDateShort(r.created_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
